import { useEffect, useState } from "react";
import api, { BACKEND_URL } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileDown, FileText, Calendar } from "lucide-react";
import { TIER_COLORS, TIER_LABELS, scoreToColor } from "@/lib/constants";
import { Link } from "react-router-dom";

export default function Reports() {
  const [items, setItems] = useState([]);
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/assessments");
        setItems(data);
        const all = await Promise.all(data.map((a) =>
          api.get(`/assessments/${a.id}/scores`).then((r) => [a.id, r.data]).catch(() => [a.id, null])
        ));
        const m = {};
        all.forEach(([id, s]) => { if (s) m[id] = s; });
        setScores(m);
      } finally { setLoading(false); }
    })();
  }, []);

  const token = localStorage.getItem("access_token");

  return (
    <div data-testid="reports-page">
      <div>
        <div className="label-uppercase">Reports</div>
        <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Executive Reports</h1>
        <p className="text-sm text-zinc-500 mt-1">Download PDF reports and CSV raw data per assessment.</p>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-zinc-500 text-sm">No assessments to report on yet.</div>}
        {items.map((a) => {
          const s = scores[a.id];
          return (
            <div key={a.id} className="rounded-md border border-zinc-800 bg-[#121212] p-5" data-testid={`report-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="label-uppercase">{a.client_name}</div>
                  <Link to={`/assessments/${a.id}`} className="text-base text-white font-medium block mt-1 hover:underline">{a.name}</Link>
                  <div className="text-[11px] text-zinc-500 mt-1 mono inline-flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" /> {a.assessment_date} · {a.assessor_name}
                  </div>
                </div>
                {s && (
                  <div className="text-right flex-shrink-0">
                    <div className="text-2xl mono font-medium" style={{ color: scoreToColor(s.overall_score) }}>{s.overall_score.toFixed(2)}</div>
                    <Badge className={`${TIER_COLORS[s.overall_tier].bg} ${TIER_COLORS[s.overall_tier].text} ${TIER_COLORS[s.overall_tier].border} text-[10px] mt-1`}>
                      {TIER_LABELS[s.overall_tier]}
                    </Badge>
                  </div>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <Button asChild className="bg-white text-black hover:bg-zinc-200 flex-1" data-testid={`pdf-${a.id}`}>
                  <a href={`${BACKEND_URL}/api/reports/assessment/${a.id}/pdf?auth=${token}`} target="_blank" rel="noreferrer">
                    <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </a>
                </Button>
                <Button asChild variant="outline" className="border-zinc-800 text-zinc-300 hover:bg-zinc-900 flex-1" data-testid={`csv-${a.id}`}>
                  <a href={`${BACKEND_URL}/api/reports/assessment/${a.id}/csv?auth=${token}`} target="_blank" rel="noreferrer">
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </a>
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
